import React, { useState, useEffect } from 'react';
import { Box, Grid, TextField, Button, Stack, Typography } from '@mui/material';

// COMPONENTS
import DateRangeSelector from '../../components/common/DateRangeSelector';
import Paginator from '../../components/common/Paginator';
import InboxSingle from '../../components/websms/inbox/InboxSingle';
import SectionWrapper from '../../components/common/SectionWrapper';
import AdvancedFiltersContainer from '../../components/common/AdvancedFiltersContainer';
import DataExportModal from '../../components/common/modals/DataExportModal';
import InboxTable from '../../components/websms/inbox/InboxTable';

// LAYOUT
import Layout from '../../components/layouts/Layout';

// ROUTES
import { WebsmsRoutes } from '../../routes/WebsmsRoutes';

// MESSAGES
import InboxFormattedMessages from '../../messages/websms/InboxFormattedMessages';

// CONFIG
import config from '../../Config';

// REQUEST
import useInboxRequest from '../../requests/InboxRequest';

const Inbox = ({ errorHandler, navigate }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [isSearching, setIsSearching] = useState(true);
  const [received, setReceived] = useState([]);
  const [messageSelected, setMessageSelected] = useState(false);
  const [message, setMessage] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromFilter, setFromFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [messageFilter, setMessageFilter] = useState('');
  const [filteredText, setFilteredText] = useState([]);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(false);
  const [showDataExportModal, setShowDataExportModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [currentlySearching, setCurrentlySearching] = useState({});
  const { searchInboxItems, requestExportInboxMessages } = useInboxRequest();

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const handleUserDataSearch = async (fromDate, toDate) => {
    setIsSearching(true);
    const searchParams = {
      fromDate,
      toDate,
      fromFilter,
      nameFilter,
      messageFilter,
      type: 'TXT',
    };
    setCurrentlySearching(searchParams);
    const inboxData = await searchInboxItems(searchParams, errorHandler);
    setReceived(inboxData);
    setIsSearching(false);
    generateSearchText();
  };

  const handleBackButton = () => {
    setMessageSelected(false);
    setMessage(null);
  };

  const changePageEvent = (page) => {
    setPageNumber(page);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  const clickedReply = (parentSms) => {
    const destTn = parentSms.dest_tn;
    const name = parentSms.dest_name || destTn;

    navigate('/websms/compose', {
      state: {
        selectedOptions: [
          {
            value: destTn,
            label: name,
            status: 'unverified',
            type: 'manual',
          },
        ],
      },
    });
  };

  const generateSearchText = () => {
    if (message) return null;

    const filterText = [];
    const filterValues = ['FROM:', 'MESSAGE:', 'NAME:'];
    [fromFilter, messageFilter, nameFilter].forEach((filter, i) => {
      if (filter !== '')
        filterText.push(
          <span key={i}>
            {filterText && filterText.length === 0 ? '' : ', '}
            <strong>{filterValues[i]}</strong> {filter}
          </span>
        );
    });

    setFilteredText(filterText);
  };

  const handlePageReset = () => {
    setMessage(null);
    setReceived([]);
    setSelectorKeyRefresh(!selectorKeyRefresh);
  };

  const clearFilters = () => {
    setFromFilter('');
    setMessageFilter('');
    setNameFilter('');
    document.getElementById('filter-form').reset();
    setFilteredText('');
    setPageNumber(1);
    handlePageReset();
  };

  const exportInboxMessages = async () => {
    const response = await requestExportInboxMessages(
      currentlySearching,
      errorHandler
    );
    setEmailRecipient(response);
    setShowDataExportModal(true);
  };

  const generateExportButton = () => {
    return (
      <Box>
        <Button
          variant="outlined"
          color="secondary"
          type="submit"
          onClick={exportInboxMessages}
        >
          Export
        </Button>
      </Box>
    );
  };

  useEffect(() => {
    if (fromDate && toDate) handleUserDataSearch(fromDate, toDate);
  }, [fromDate, toDate]);

  return (
    <Layout routes={WebsmsRoutes} title={InboxFormattedMessages.inboxTitle}>
      {messageSelected ? (
        <InboxSingle
          message={message}
          clickedReply={clickedReply}
          errorHandler={errorHandler}
          backButtonClick={handleBackButton}
        />
      ) : (
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            rowGap: 2,
          }}
        >
          <Grid size={{ xs: 12, lg: 9 }}>
            <SectionWrapper
              title="Search Inbox"
              icon="search"
              sx={{ maxWidth: 1000 }}
            >
              <DateRangeSelector
                key={selectorKeyRefresh ? 1 : 0}
                handleDateSearch={handleUserDataSearch}
                parentStartDate={fromDate}
                setParentStartDate={setFromDate}
                parentEndDate={toDate}
                setParentEndDate={setToDate}
              />

              <AdvancedFiltersContainer>
                <Box component="form" id="filter-form">
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      marginY: 2,
                      alignItems: { lg: 'center' },
                      maxWidth: { lg: 674 },
                    }}
                  >
                    <Stack>
                      <Typography sx={{ whiteSpace: 'nowrap' }}>
                        By Field:
                      </Typography>
                    </Stack>

                    <Stack sx={{ gap: 1, flex: 1 }}>
                      <Stack
                        sx={{
                          flexDirection: { lg: 'row' },
                          gap: 2,
                          alignItems: { lg: 'center' },
                        }}
                      >
                        <TextField
                          label="From"
                          value={fromFilter}
                          onChange={handleInputChange(setFromFilter)}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Name"
                          value={nameFilter}
                          onChange={handleInputChange(setNameFilter)}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Message"
                          value={messageFilter}
                          onChange={handleInputChange(setMessageFilter)}
                          size="small"
                          fullWidth
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              </AdvancedFiltersContainer>

              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {filteredText && filteredText.length !== 0 && (
                  <Button
                    color="secondary"
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      textDecoration: 'underline',
                      fontWeight: 400,
                    }}
                  >
                    {InboxFormattedMessages.clear}
                  </Button>
                )}

                <Button
                  onClick={() => {
                    pageNumber === 1
                      ? handleUserDataSearch(fromDate, toDate)
                      : setPageNumber(1);
                  }}
                  color="secondary"
                  variant="outlined"
                >
                  {InboxFormattedMessages.searchButton}
                </Button>
              </Stack>
            </SectionWrapper>
          </Grid>
          <Grid size={12}>
            <Stack>
              {filteredText && filteredText.length !== 0 && (
                <Typography fontSize={12} marginBottom={1}>
                  {filteredText}
                </Typography>
              )}
            </Stack>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                marginBottom: 1,
              }}
            >
              {generateExportButton()}
            </Stack>

            <InboxTable
              received={received.slice(
                (pageNumber - 1) * pageLength,
                (pageNumber - 1) * pageLength + pageLength
              )}
              isSearching={isSearching}
              setMessageSelected={setMessageSelected}
              setMessage={setMessage}
            />

            <Paginator
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              itemCount={received.length ?? 0}
              changeEvent={changePageEvent}
              pageLength={pageLength}
              editFunction={(e) => changePageLength(e)}
              showTotal={'Found'}
              toolTip={'Maximum of 4,000 records shown'}
            />
          </Grid>
        </Grid>
      )}
      <DataExportModal
        show={showDataExportModal}
        onHide={() => setShowDataExportModal(false)}
        emailRecipient={emailRecipient}
      />
    </Layout>
  );
};

export default Inbox;
